import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { firstValueFrom, Subscription } from 'rxjs';

import { StudioSidebarService } from '../../services/studio-sidebar/studio-sidebar.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { RecordingsService } from '../../services/recordings/recordings.service';
import { DescriptFile, Recording } from '@sc/types';
import { DownloadAllMenuComponent } from '../../menus/download-all-menu/download-all-menu.component';
import { EditTakeNameMenuComponent } from '../../menus/edit-take-name-menu/edit-take-name-menu.component';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { RoleLimits } from '@sc/types';
import { RolesService } from '../../services/roles/roles.service';
import { Take } from '@sc/types';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
import { IdTokenService } from '../../services/id-token/id-token.service';
import { DescriptService } from '../../services/descript/descript.service';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'sc-recordings-take',
  templateUrl: './recordings-take.component.html',
  styleUrls: ['./recordings-take.component.scss'],
})
export class RecordingsTakeComponent implements OnInit, OnDestroy {
  @Input() takeID: string;
  @Output() selectTakeID = new EventEmitter<string>();
  @Output() closeSidebar = new EventEmitter<boolean>();

  role$ = this.rolesService.role$;
  take: Take;

  roleLimits = RoleLimits;
  recordings: Recording[];
  recordingsReady = false;
  subs: Array<Subscription> = [];

  constructor(
    private popoverController: PopoverController,
    private sessionsService: SessionsService,
    private studioSidebarService: StudioSidebarService,
    private recordingsService: RecordingsService,
    private analyticsService: AnalyticsService,
    private rolesService: RolesService,
    private idTokenService: IdTokenService,
    private descriptService: DescriptService,
    private toastrService: ToastrService
  ) {}

  async ngOnInit() {
    this.take = await firstValueFrom(this.recordingsService.getTake(this.takeID));
    this.setupTake();
    this.setupRecordings();
  }

  setupTake() {
    const sub = this.recordingsService.getTake(this.takeID).subscribe((take) => {
      this.take = take;
    });

    this.subs.push(sub);
  }

  /**
   * Loads all recordings for this take
   */
  setupRecordings() {
    const sub = this.recordingsService
      .getAllRecordingsFromTake(this.take.sessionID, this.take.takeNumber)
      .subscribe((recordings) => {
        this.recordings = recordings;
        const ready = recordings.filter((recording) => {
          if (
            recording.preview ||
            recording.screenRecording ||
            (recording.videoPercentage && recording.videoPercentage < 100) ||
            (recording.percentage && recording.percentage < 100)
          ) {
            return false;
          }
          return true;
        });
        this.recordingsReady = ready.length > 0;
      });
    this.subs.push(sub);
  }

  /**
   * Sends recordings from selected take to Edit in Descript
   *
   */
  async editInDescript() {
    const recordingsFromTake = await firstValueFrom(
      this.recordingsService.getAllRecordingsFromTake(this.take.sessionID, this.take.takeNumber)
    );
    this.descriptService.editInDescript(recordingsFromTake);
  }

  /**
   * Opens the download all menu
   *
   * @param click - MouseEvent
   */
  async openDownloadAllMenu(click: MouseEvent) {
    const recordings = await firstValueFrom(
      this.sessionsService.getAllRecordingsFromTake(this.take.sessionID, this.take.takeNumber)
    );

    const popover = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: DownloadAllMenuComponent,
      componentProps: {
        session: this.take.sessionID,
        recordings,
        all: true,
        event: click,
      },
      event: click,
    });

    await popover.present();
    await this.analyticsService.track('opened download all');
  }

  async openEditTakeNameMenu(click: MouseEvent) {
    const popover = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: EditTakeNameMenuComponent,
      componentProps: { take: this.take },
      event: click,
    });

    await popover.present();
    await this.analyticsService.track('opened edit take name');
  }

  /**
   * Resets the active chat group
   * Returns to chat group
   */
  async goBack() {
    this.selectTakeID.emit(null);
    await this.analyticsService.track('clicked back');
  }

  /**
   * Prevents flickering/flashing in the list of recordings
   *
   * @param index - number
   * @param recording - Recording
   * @returns - string
   */
  trackBy(index: number, recording: Recording) {
    return recording.recordingID;
  }

  ngOnDestroy(): void {
    this.subs.map((sub) => sub.unsubscribe());
  }
}
